import { X } from 'lucide-react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

import { SelectItemContent } from './single-select-option-renderers';
import { SingleSelectProps } from './single-select-types';

interface SimpleSingleSelectProps<T extends FieldValues>
  extends Pick<
    SingleSelectProps<T>,
    | 'name'
    | 'control'
    | 'options'
    | 'label'
    | 'placeholder'
    | 'noOptionsMessage'
    | 'disabled'
    | 'loading'
    | 'className'
    | 'formItemClassName'
    | 'showFormMessage'
    | 'showAvatars'
    | 'showDescriptionInOptions'
    | 'avatarShape'
    | 'avatarSize'
    | 'renderOption'
    | 'renderTrigger'
    | 'onChange'
    | 'warning'
    | 'initialSelectedItem'
    | 'isClearSelectionEnabled'
  > {
  optionsKey: number;
}

export function SimpleSingleSelect<T extends FieldValues>({
  name,
  control,
  options,
  label,
  placeholder,
  noOptionsMessage,
  disabled = false,
  loading = false,
  className,
  formItemClassName,
  showFormMessage = true,
  showAvatars = false,
  showDescriptionInOptions = false,
  avatarShape = 'circle',
  avatarSize,
  renderOption,
  renderTrigger,
  onChange,
  optionsKey,
  warning,
  initialSelectedItem,
  isClearSelectionEnabled = false,
}: SimpleSingleSelectProps<T>) {
  const { t } = useTranslation(['common']);

  const defaultPlaceholder = placeholder || t('selectOption');
  const defaultNoOptionsMessage = noOptionsMessage || t('noOptionsAvailable');

  return (
    <FormField
      control={control}
      name={name}
      warning={warning}
      render={({ field }) => {
        const fieldValue = field.value?.toString() || '';

        // Determine the selected option:
        // 1. If initialSelectedItem is provided and matches current value, use it
        // 2. Otherwise, find the option in the options array
        const selectedOption =
          (initialSelectedItem && initialSelectedItem.value === field.value
            ? initialSelectedItem
            : null) ||
          options.find(opt => opt.value === field.value) ||
          null;

        const handleClear = (e: React.MouseEvent | React.PointerEvent) => {
          e.stopPropagation();
          e.preventDefault();
          field.onChange('');
          onChange?.('');
        };

        return (
          <FormItem className={cn('w-full', formItemClassName)}>
            {label && <FormLabel>{label}</FormLabel>}
            <Select
              key={`${name}-${optionsKey}`}
              value={fieldValue}
              onValueChange={value => {
                // Guard against Radix firing onValueChange("") during controlled value transitions
                if (!value) return;
                field.onChange(value);
                if (onChange) {
                  onChange(value);
                }
              }}
              disabled={disabled || loading}
            >
              <FormControl>
                <SelectTrigger hasWarning={!!warning} className={className}>
                  {renderTrigger && field.value ? (
                    renderTrigger(selectedOption, defaultPlaceholder)
                  ) : selectedOption && !options.find(opt => opt.value === field.value) ? (
                    // Value set programmatically but option not in the list yet — show label directly
                    <span className="truncate">{selectedOption.label}</span>
                  ) : (
                    <SelectValue placeholder={loading ? t('loadingOptions') : defaultPlaceholder} />
                  )}
                  {isClearSelectionEnabled && fieldValue && !disabled && !loading && (
                    <button
                      type="button"
                      onPointerDown={handleClear}
                      className="ml-auto mr-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:bg-accent hover:text-foreground"
                      aria-label={t('clearSelection')}
                    >
                      <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                    </button>
                  )}
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {options.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    {defaultNoOptionsMessage}
                  </div>
                ) : (
                  options.map(option => {
                    const isSelected = option.value === fieldValue;
                    return (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        checkPosition="right"
                        itemTextAsChild={!!option.customContent || !!renderOption}
                      >
                        {option.customContent ||
                          (renderOption ? (
                            renderOption(option, isSelected)
                          ) : (
                            <SelectItemContent
                              option={option}
                              showAvatars={showAvatars}
                              showDescriptionInOptions={showDescriptionInOptions}
                              avatarShape={avatarShape}
                              avatarSize={avatarSize}
                              isSelected={isSelected}
                            />
                          ))}
                      </SelectItem>
                    );
                  })
                )}
              </SelectContent>
            </Select>
            {showFormMessage && <FormMessage />}
          </FormItem>
        );
      }}
    />
  );
}
